import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import HistoryLog from '../HistoryLog';
import { EVENT_TIMELINE_ENTITY_TYPES, EVENT_TIMELINE_SUB_TYPES } from 'App/babel/enum/timeline';
import ContactTimelineCreateContact from './ContactTimelineCreateContact';
import { getTimelineRow } from '../../../babel/components/Helpers/getTimelineRow';
import T from 'Components/Helpers/translate';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Contact from 'App/resources/Model/Contact';
import usePartialLoader, { type FetcherFunction } from 'App/components/hooks/usePartialLoader';
import type { HistoryLogRenderedBy } from 'App/components/HistoryLog/ModalHistoryLog';
import { TimelineComponentProps } from 'Components/TimelineRow/TimelineGeneric/TimelineCardConfigs';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import Event from 'App/resources/Model/Event';
import HistoryLogSearch from '../HistoryLogSearch';
import { expandHistoryLogSearch } from '../historyLogSearchSynonyms';

const listeners = ['comment.added', 'comment.deleted', 'comment.updated'];

type Props = {
	contact: Contact;
	client: Contact['client'];
	notesFilterActive?: boolean;
	renderedBy: HistoryLogRenderedBy;
	selectButtons?: ReactNode;
	cardAdditionalProps?: Partial<TimelineComponentProps>;
};

const fetcher: FetcherFunction<Pick<Props, 'notesFilterActive' | 'contact'> & { search?: string }, Event> = (
	filters,
	{ contact, notesFilterActive, search }
) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.contacts.attr?.id, ComparisonTypes.Equals, contact.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);
	filters.addFilter({ field: 'feedContact2' }, ComparisonTypes.Equals, null);
	filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['Contact']);

	if (notesFilterActive) {
		filters.addFilter({ field: 'feedNotes' }, ComparisonTypes.Equals, null);
	}

	if (search) {
		filters.addFilter(event.attr.search, ComparisonTypes.Equals, expandHistoryLogSearch(search));
	}

	filters.addSort(event.attr.date, false);
	filters.addSort(event.attr.entityType, false);
	return event.find(filters.build());
};

const ContactHistoryLog = ({ contact, client, renderedBy, notesFilterActive, selectButtons }: Props) => {
	const [search, setSearch] = useState('');
	const onSearchChange = useCallback((value: string) => setSearch(value), []);

	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore,
		reset
	} = usePartialLoader({
		fetcher,
		broadcastTypes: listeners,
		fetcherProps: {
			contact,
			notesFilterActive,
			search
		}
	});

	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		reset();
	}, [search]);

	// Latch search visibility so clearing the query doesn't unmount the input
	// (which would steal focus) while the timeline refetches.
	const hasShownSearchRef = useRef(false);
	if (totalEvents > 0 || search) {
		hasShownSearchRef.current = true;
	}
	const showSearchInput = hasShownSearchRef.current;

	if (!contact || !contact.id) {
		return null;
	}
	let timelineElements: ReactNode[] = [];
	if (events && events.length) {
		timelineElements = events.map(event => {
			return event ? (
				<div key={event.id}>{getTimelineRow(event, 'Contact', notesFilterActive, false, renderedBy)}</div>
			) : null;
		});
	}

	const contactRegEvent = {
		entityType: EVENT_TIMELINE_ENTITY_TYPES.CONTACT,
		subType: EVENT_TIMELINE_SUB_TYPES.CREATED,
		users: contact.regBy ? [contact.regBy] : [],
		entityId: contact.id,
		client: client,
		contact: contact
	};

	return (
		<HistoryLog
			title={T('history')}
			selectButtons={selectButtons}
			searchInput={showSearchInput ? <HistoryLogSearch onSearchChange={onSearchChange} /> : null}
			loading={loading}
		>
			{timelineElements}
			{timelineElements.length < totalEvents ? (
				<TimelineLoadMore
					newEventsLoading={loading}
					numEventsLoaded={events.length}
					totalEvents={totalEvents ? totalEvents : null}
					incrementOffset={loadMore}
				/>
			) : null}
			<ContactTimelineCreateContact isHistoryLog event={contactRegEvent} />
		</HistoryLog>
	);
};

export default ContactHistoryLog;
